import { useQueryClient } from '@tanstack/react-query';
import { Download, Loader2 } from 'lucide-react';
import { lazy, Suspense, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '~/components/ui/button';
import { Card, CardContent } from '~/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { Skeleton } from '~/components/ui/skeleton';

import { useArmLabels } from '../../hooks/use-arm-labels';
import {
  prefetchAnalyticsPerformanceTrends,
  useAnalyticsPerformanceTrend,
} from '../../hooks/use-experiment-analytics';
import { downloadAnalyticsExperimentCsv } from '../../services/analytics.service';
import type { AnalyticsGrain } from '../../types/analytics-controls.types';
import type { ArmSlot } from '../../types/experiment.types';
import { PerformanceMetric } from '../../types/experiment.types';
import { formatPercentFromPercent } from '../../utils/format-percent';
import { mapPerformanceTrendToChartData } from '../../utils/map-performance-trend';

import { ExperimentSeriesToggle } from './experiment-series-toggle';
import type { TrendAnnotation, TrendSeries } from './experiment-trend-chart';
import { ExperimentTrendEmptyState } from './experiment-trend-empty-state';
import { RevenueSourceTag } from './revenue-source-tag';

// Lazy-loaded so echarts (~186 KB gzip) stays out of the page chunk and
// downloads in parallel with the rest of the panel.
const ExperimentTrendChart = lazy(() =>
  import('./experiment-trend-chart').then((m) => ({ default: m.ExperimentTrendChart })),
);

interface Props {
  experimentId: string;
  grain: AnalyticsGrain;
  /** Encoded `?segment=` product filter (see `encodeSegmentFilter`); `undefined` = unscoped. */
  segment?: string | undefined;
}

interface SeriesMeta {
  id: ArmSlot;
  color: 'blue' | 'green' | 'orange';
  lineStyle: 'solid' | 'dashed';
}

// Slot ids only — each slot's display label (its domain arm letter) is
// resolved at render time via `useArmLabels`.
const SERIES_META: readonly SeriesMeta[] = [
  { id: 'testA', color: 'blue', lineStyle: 'solid' },
  { id: 'testB', color: 'green', lineStyle: 'solid' },
  { id: 'control', color: 'orange', lineStyle: 'dashed' },
];

const METRIC_OPTIONS: { value: PerformanceMetric; labelKey: string }[] = [
  { value: PerformanceMetric.CTR_LIFT, labelKey: 'detail.performance.metrics.ctrLift' },
  { value: PerformanceMetric.CVR_LIFT, labelKey: 'detail.performance.metrics.cvrLift' },
  { value: PerformanceMetric.ROAS_LIFT, labelKey: 'detail.performance.metrics.roasLift' },
  { value: PerformanceMetric.CPC_LIFT, labelKey: 'detail.performance.metrics.cpcLift' },
  { value: PerformanceMetric.REVENUE_LIFT, labelKey: 'detail.performance.metrics.revenueLift' },
  {
    value: PerformanceMetric.CONV_VALUE_LIFT,
    labelKey: 'detail.performance.metrics.convValueLift',
  },
  {
    value: PerformanceMetric.CONVERSIONS_LIFT,
    labelKey: 'detail.performance.metrics.conversionsLift',
  },
  {
    value: PerformanceMetric.IMPRESSIONS_LIFT,
    labelKey: 'detail.performance.metrics.impressionsLift',
  },
];

const Y_AXIS_KEY: Record<PerformanceMetric, string> = {
  [PerformanceMetric.REVENUE_LIFT]: 'detail.performance.yAxisLabel.revenueLift',
  [PerformanceMetric.CONV_VALUE_LIFT]: 'detail.performance.yAxisLabel.convValueLift',
  [PerformanceMetric.CONVERSIONS_LIFT]: 'detail.performance.yAxisLabel.conversionsLift',
  [PerformanceMetric.IMPRESSIONS_LIFT]: 'detail.performance.yAxisLabel.impressionsLift',
  [PerformanceMetric.CTR_LIFT]: 'detail.performance.yAxisLabel.ctrLift',
  [PerformanceMetric.CVR_LIFT]: 'detail.performance.yAxisLabel.cvrLift',
  [PerformanceMetric.ROAS_LIFT]: 'detail.performance.yAxisLabel.roasLift',
  [PerformanceMetric.CPC_LIFT]: 'detail.performance.yAxisLabel.cpcLift',
};

const ANNOTATION_KEYS: Record<string, string> = {
  'Day 7 Check': 'detail.performance.annotations.day7Check',
  'DID Check': 'detail.performance.annotations.didCheck',
};

// Cumulative-trend values arrive from `mapAnalyticsPerformanceToLegacy`
// already expressed as percent (e.g. `-46.5` for −46.5%), so use the
// `FromPercent` variant. Keeping this alias readable at the JSX call site.
const formatYAxis = formatPercentFromPercent;

export function ExperimentPerformancePanel({ experimentId, grain, segment }: Props) {
  const { t } = useTranslation('experiments');
  const queryClient = useQueryClient();
  const [metric, setMetric] = useState<PerformanceMetric>(PerformanceMetric.CTR_LIFT);
  const [visibleSeriesIds, setVisibleSeriesIds] = useState(['testA', 'testB', 'control']);
  // Wire test slots map onto domain arm letters (testA → Arm B, testB → Arm C);
  // `hasTestB` is ground truth for arm count — analytics armsPresent can lag for
  // 3-arm experiments.
  const { label: armLabel, hasTestB } = useArmLabels(experimentId);
  // Disables the button while a download is in flight so a frustrated
  // double-click can't kick off two parallel fetches against the analytics
  // service.
  const [isExporting, setIsExporting] = useState(false);

  // Live analytics is the sole source of truth — DRAFT experiments and runs
  // with no ingest yet render an empty chart rather than seeded placeholder
  // numbers, so operators never see fabricated lift values. The endpoint
  // already computes cumulative lift server-side (scoped to post-launch
  // days), so no client-side date clamping is needed here.
  const trendQuery = useAnalyticsPerformanceTrend(experimentId, metric, segment);

  const data = useMemo(() => {
    if (trendQuery.data && trendQuery.data.arms.length > 0) {
      return mapPerformanceTrendToChartData(trendQuery.data);
    }
    return undefined;
  }, [trendQuery.data]);

  const isLoading = trendQuery.isLoading;
  // True only on a metric-dropdown change once a chart is already on screen —
  // `keepPreviousData` (see the hook) keeps rendering the prior metric's chart
  // underneath a blurred "updating" overlay instead of flashing it blank.
  const isRefetching = trendQuery.isFetching && !isLoading;

  // Distinguish "still loading" from "loaded but truly empty post-clamp" so
  // we can show the empty state only in the second case. Without this, a
  // freshly-launched Day-1 experiment renders a blank chart with no
  // explanation — operators have no idea whether something broke or if
  // they're just early.
  const hasPostLaunchData = Boolean(data && data.series.length > 0);
  const showEmptyState = !isLoading && !hasPostLaunchData;

  const xAxisCategories = useMemo(
    () => (data ? data.series.map((p) => `Day ${String(p.day)}`) : []),
    [data],
  );

  // The subset of SERIES_META for arms that should render. Arm C is gated on
  // experiment.armCount (ground truth) so the toggle never appears on a 2-arm
  // experiment even if analytics data hasn't arrived yet. testA/control are
  // gated by the analytics armsPresent signal as before.
  const presentMeta = useMemo<readonly SeriesMeta[]>(() => {
    if (!data) return hasTestB ? SERIES_META : SERIES_META.filter((m) => m.id !== 'testB');
    const present = new Set<ArmSlot>(data.armsPresent);
    return SERIES_META.filter((meta) => {
      if (meta.id === 'testB') return hasTestB;
      return present.has(meta.id);
    });
  }, [data, hasTestB]);

  const series = useMemo<TrendSeries[]>(() => {
    if (!data) return [];
    return presentMeta.map((meta) => ({
      id: meta.id,
      name: armLabel(meta.id),
      color: meta.color,
      lineStyle: meta.lineStyle,
      points: data.series.map((p) => ({
        x: `Day ${String(p.day)}`,
        y: p[meta.id],
      })),
    }));
  }, [data, presentMeta, armLabel]);

  const annotations = useMemo<TrendAnnotation[]>(() => {
    if (!data) return [];
    return data.annotations.map((a) => {
      const key = ANNOTATION_KEYS[a.label];
      return {
        x: `Day ${String(a.day)}`,
        label: key ? t(key) : a.label,
      };
    });
  }, [data, t]);

  const handleExportCsv = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      await downloadAnalyticsExperimentCsv(experimentId, { grain, segment });
    } catch {
      toast.error(t('detail.performance.exportCsvError'));
    } finally {
      setIsExporting(false);
    }
  };

  const toggleSeries = (id: string, checked: boolean) => {
    setVisibleSeriesIds((prev) =>
      checked
        ? [...prev, id].filter((v, i, arr) => arr.indexOf(v) === i)
        : prev.filter((v) => v !== id),
    );
  };

  return (
    <Card className="min-w-0 overflow-hidden">
      <CardContent className="min-w-0 space-y-6 p-6">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="flex items-center gap-2 text-[16px] font-medium">
              {t('detail.performance.cumulativeTrend.title')}
              {/* Non-null only for the revenue / ROAS lift metrics. */}
              <RevenueSourceTag source={trendQuery.data?.revenue_source} />
            </h3>
            <p className="text-muted-foreground text-sm">
              {t('detail.performance.cumulativeTrend.subtitle')}
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={isExporting ? 'cursor-not-allowed' : 'cursor-pointer'}
            onClick={() => {
              void handleExportCsv();
            }}
            disabled={isExporting}
            aria-busy={isExporting}
          >
            <Download className="mr-2 h-4 w-4" aria-hidden />
            {isExporting
              ? t('detail.performance.exportCsvBusy')
              : t('detail.performance.exportCsv')}
          </Button>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Select
            value={metric}
            onValueChange={(value) => {
              setMetric(value as PerformanceMetric);
            }}
          >
            <SelectTrigger className="w-[260px] cursor-pointer">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {METRIC_OPTIONS.map((opt) => (
                <SelectItem
                  key={opt.value}
                  value={opt.value}
                  className="cursor-pointer"
                  // Warm only the metric the user is about to pick (Radix focuses the
                  // highlighted item on hover and arrow keys) — not all eight on open.
                  onFocus={() => {
                    prefetchAnalyticsPerformanceTrends(
                      queryClient,
                      experimentId,
                      [opt.value],
                      segment,
                    );
                  }}
                >
                  {t(opt.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex flex-wrap gap-2">
            {presentMeta.map((meta) => (
              <ExperimentSeriesToggle
                key={meta.id}
                color={meta.color}
                label={armLabel(meta.id)}
                checked={visibleSeriesIds.includes(meta.id)}
                onCheckedChange={(v) => {
                  toggleSeries(meta.id, v);
                }}
              />
            ))}
          </div>
        </div>

        {/* Chart (or empty state when the experiment has no post-launch data yet) */}
        {showEmptyState ? (
          <ExperimentTrendEmptyState
            title={t('detail.performance.cumulativeTrend.emptyTitle')}
            subtitle={t('detail.performance.cumulativeTrend.emptySubtitle')}
          />
        ) : (
          <div className="relative">
            <Suspense fallback={<Skeleton className="h-80 w-full" />}>
              <ExperimentTrendChart
                xAxisCategories={xAxisCategories}
                series={series}
                visibleSeriesIds={visibleSeriesIds}
                annotations={annotations}
                yAxisLabel={t(Y_AXIS_KEY[metric])}
                yAxisFormatter={formatYAxis}
                isLoading={isLoading}
              />
            </Suspense>
            {/* Metric-switch refresh: blur the still-visible prior chart
                instead of clearing it, so the axes/lines don't flash blank. */}
            {isRefetching && (
              <div
                role="status"
                className="bg-background/60 absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-md backdrop-blur-[1px]"
              >
                <Loader2
                  className="text-muted-foreground h-5 w-5 animate-spin"
                  aria-hidden="true"
                />
                <span className="text-muted-foreground text-sm font-medium">
                  {t('detail.performance.cumulativeTrend.updating', 'Loading…')}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <p className="text-muted-foreground text-sm">
          {t('detail.performance.cumulativeTrend.footer')}
        </p>
      </CardContent>
    </Card>
  );
}
